<template>
    <div>
        <loading-progress-bar v-if="loading" :loading="loading" color="secondary"></loading-progress-bar>
        <v-layout row wrap v-if="!loading">
            <v-flex xs12>
                <vue-echart :white-mode="whiteMode" :config="ecConfig" :height="height"></vue-echart>
            </v-flex>
        </v-layout>
    </div>
</template>

<script>
import {callAPI, sortFloors} from '@/helper'
import LoadingProgressBar from './../LoadingProgressBar'
import VueEchart from "@/components/VueEchart";

export default {
    props: {
        title: {type: String, default: "Energy Breakdown"},
        powerSensor: {type: String, default: "Power"},
        unit: {type: String, default: "kWh"},
        aggFrequency: {type: String, default: null},
        dateRange: {type: Object, default: null},
        whiteMode: {type: Boolean, default: false},
        height: {type: Number, default: 490},
    },
    components: {LoadingProgressBar, VueEchart},
    computed: {},
    name: 'TreeMapWidget',
    watch: {
        dateRange() {
            this.refresh()
        },
    },
    data: function () {
        return {
            loading: true,
            timeout: null,
            reloadInc:0,
            ecConfig: null,
        }
    },
    mounted() {
        this.refresh()
    },
    methods: {
        debounce(callback, wait) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => callback(), wait);
        },
        refresh() {
            this.debounce(this.getData, 500);
        },
        findWhere(array, key, value) {
            let t = 0;
            while (t < array.length && array[t][key] !== value) {
                t++;
            }
            if (t < array.length) {
                return array[t]
            } else {
                return false;
            }
        },
        arrangeIntoTree(paths) {
            let tree = [];
            for (let i = 0; i < paths.length; i++) {
                let path = paths[i];
                let currentLevel = tree;
                for (let j = 0; j < path.length; j++) {
                    let part = path[j];
                    let existingPath = this.findWhere(currentLevel, 'name', part);
                    if (existingPath) {
                        currentLevel = existingPath.children;
                    } else {
                        let newPart = {
                            name: part,
                            path: path.join('>')+'>'+part,
                            children: [],
                        }
                        currentLevel.push(newPart);
                        currentLevel = newPart.children;
                    }
                }
            }
            return tree;
        },
        applyValues(tree,values){
            for (let node of tree){
                if (node.children&&node.children.length>0){
                    this.applyValues(node.children,values)
                    node.value=node.children.reduce((sum, x) => sum + x.value, 0)
                } else if (values[node.name]){
                    node.value=values[node.name]
                } else {
                    node.value=0
                }
            }
        },
        async getData() {
            this.loading = true
            this.reloadInc=this.reloadInc+1
            let currentInc=this.reloadInc
            let me=this
            let dateRange = this.dateRange ? [this.dateRange.start, this.dateRange.end.length < 12 ? this.dateRange.end + " 23:59:59" : this.dateRange.end] : null;
            let payload =
                {
                    "filters": {
                        "sensor_name": this.powerSensor,
                        "facility_name": this.$store.state.pointsListStore.pointsList.facility_name
                    },
                    "pipeline": [],
                    "date_range": dateRange
                }
            if (this.aggFrequency) {
                payload.pipeline.unshift({
                    "type": "agg",
                    "agg": this.aggFrequency,
                    "agg_op": "sum",
                    "divide_by_sf": true
                })
            }
            let result = await callAPI('data3/', payload)
            if (currentInc!==this.reloadInc){
                return
            }
            let items = []
            let values={}
            for (let i in this.$store.state.pointsListStore.pointsList.sensors) {
                let current = JSON.parse(JSON.stringify(this.$store.state.pointsListStore.pointsList.equipment_details[i]))
                if(this.$store.state.pointsListStore.pointsList.sensors[i].includes(this.powerSensor)&&result.data[current.equipment_name+" - "+this.powerSensor]){
                    values[current.equipment_name]=result.data[current.equipment_name+" - "+this.powerSensor].reduce((acc, curr) => curr!==null ? curr : acc)
                    items.push(current)
                }
            }
            items.sort((a, b) => (a.equipment_name > b.equipment_name) ? 1 : -1)
            let equipPaths=[];
            for (let equip of items){
                if(equip.floor&&equip.floor!==""&&equip.floor!=="NaN"){
                    if (equip.floor.startsWith(".")&&this.$store.state.userStore.user.content.role!=="admin"){
                        continue
                    }
                    equipPaths.push(equip.floor.split('>').concat([equip.equipment_name]))
                } else if (equip.equipment_type&&equip.equipment_type!==""&&equip.equipment_type!==equip.equipment_name){
                    equipPaths.push([equip.equipment_type,equip.equipment_name])
                } else {
                    equipPaths.push([equip.equipment_name])
                }
            }
            equipPaths=sortFloors(equipPaths)
            let tree=this.arrangeIntoTree(equipPaths);
            this.applyValues(tree,values)
            function getLevelOption() {
                return [
                    {
                        itemStyle: {
                            borderColor: '#777',
                            borderWidth: 0,
                            gapWidth: 1
                        },
                        upperLabel: {
                            show: false
                        }
                    },
                    {
                        itemStyle: {
                            borderColor: '#555',
                            borderWidth: 5,
                            gapWidth: 1
                        },
                        emphasis: {
                            itemStyle: {
                                borderColor: '#ddd'
                            }
                        }
                    },
                    {
                        colorSaturation: [0.35, 0.5],
                        itemStyle: {
                            borderWidth: 5,
                            gapWidth: 1,
                            borderColorSaturation: 0.6
                        }
                    }
                ];
            }
            this.ecConfig={
                title: {
                    text: this.title,
                    left: 'center'
                },
                tooltip: {
                    formatter: function (info) {
                        var value = info.value;
                        var treePathInfo = info.treePathInfo;
                        var treePath = [];
                        for (var i = 1; i < treePathInfo.length; i++) {
                            treePath.push(treePathInfo[i].name);
                        }
                        return [
                            '<div class="tooltip-title">' +
                            treePath.join('>') +
                            '</div>',
                             value.toLocaleString() + ' '+me.unit
                        ].join('');
                    }
                },
                series: [
                    {
                        name: this.title,
                        type: 'treemap',
                        visibleMin: 300,
                        label: {
                            show: true,
                            formatter: '{b}'
                        },
                        upperLabel: {
                            show: true,
                            height: 30
                        },
                        itemStyle: {
                            borderColor: '#fff'
                        },
                        levels: getLevelOption(),
                        data: tree
                    }
                ]
            }
            this.loading=false
        }
    },
}

</script>

<style scoped>

</style>
